import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/lib/utils';

import type { DocumentPolicy, DocumentPolicyFormData } from '@/types/documentPolicy.types';

type ModalMode = 'create' | 'edit' | 'clone';

interface PolicyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: DocumentPolicyFormData) => Promise<void>;
  mode: ModalMode;
  policy?: DocumentPolicy;
}

const ROLE_OPTIONS = ['armed', 'unarmed', 'supervisor', 'patrol', 'event'] as const;
const SITE_OPTIONS = ['_all', 'FL', 'NY'] as const;
const MIME_OPTIONS = ['image/jpeg', 'image/png', 'application/pdf'] as const;

const policySchema = z
  .object({
    name: z.string().min(1),
    role: z.string().min(1),
    site: z.string().min(1),
    requiredTypes: z.array(z.string()),
    optionalTypes: z.array(z.string()).optional(),
    allowedMime: z.array(z.string()).min(1),
    maxSizeMb: z.number().min(1),
    expirationThresholdDays: z.number().min(1).optional(),
    ocrEnabled: z.boolean(),
  })
  // A policy may have zero required types (e.g. an all-optional policy like
  // "Certifications"), but it must declare at least one document type overall.
  .refine((data) => data.requiredTypes.length > 0 || (data.optionalTypes?.length ?? 0) > 0, {
    path: ['requiredTypes'],
    message: 'At least one document type is needed',
  });

const labelClassName = 'block text-sm font-medium text-gray-700';
const inputClassName =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function buildInitialFormData(policy?: DocumentPolicy): DocumentPolicyFormData {
  if (!policy) {
    return {
      name: '',
      role: '',
      site: '_all',
      requiredTypes: [],
      optionalTypes: [],
      allowedMime: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeMb: 10,
      ocrEnabled: false,
    };
  }

  return {
    name: policy.name,
    role: policy.role,
    site: policy.site,
    requiredTypes: [...policy.requiredTypes],
    optionalTypes: policy.optionalTypes ? [...policy.optionalTypes] : [],
    allowedMime: [...policy.allowedMime],
    maxSizeMb: policy.maxSizeMb,
    // The API may return null (not undefined) for an unset threshold; coerce it
    // so the `z.number().optional()` schema (which rejects null) accepts it.
    expirationThresholdDays: policy.expirationThresholdDays ?? undefined,
    ocrEnabled: policy.ocrEnabled,
  };
}

export function PolicyFormModal({ isOpen, onClose, onSave, mode, policy }: PolicyFormModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<DocumentPolicyFormData>(() => buildInitialFormData(policy));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiredTypeInput, setRequiredTypeInput] = useState('');
  const [optionalTypeInput, setOptionalTypeInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      const initial = buildInitialFormData(mode === 'clone' || mode === 'edit' ? policy : undefined);
      if (mode === 'clone') {
        initial.name = `${initial.name} (copy)`;
      }
      setFormData(initial);
      setErrors({});
      setRequiredTypeInput('');
      setOptionalTypeInput('');
    }
  }, [isOpen, mode, policy]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});

      const result = policySchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const path = issue.path.join('.');
          fieldErrors[path] = t(`documentPolicies.validation.${path}`, issue.message);
        }
        setErrors(fieldErrors);
      } else {
        setIsSubmitting(true);
        try {
          await onSave(result.data);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formData, onSave, t],
  );

  const addTag = useCallback(
    (field: 'requiredTypes' | 'optionalTypes', value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (formData[field]?.includes(trimmed)) return;
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] ?? []), trimmed],
      }));
    },
    [formData],
  );

  const removeTag = useCallback((field: 'requiredTypes' | 'optionalTypes', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] ?? []).filter((_, i) => i !== index),
    }));
  }, []);

  const handleTagKeyDown = useCallback(
    (
      field: 'requiredTypes' | 'optionalTypes',
      input: string,
      setInput: (v: string) => void,
      e: React.KeyboardEvent,
    ) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag(field, input);
        setInput('');
      }
    },
    [addTag],
  );

  const toggleMime = useCallback((mime: string) => {
    setFormData((prev) => {
      const has = prev.allowedMime.includes(mime);
      return {
        ...prev,
        allowedMime: has ? prev.allowedMime.filter((m) => m !== mime) : [...prev.allowedMime, mime],
      };
    });
  }, []);

  if (!isOpen) return null;

  const titleKey =
    mode === 'create'
      ? 'documentPolicies.actions.create'
      : mode === 'clone'
        ? 'documentPolicies.actions.clone'
        : 'documentPolicies.actions.edit';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>{t(titleKey)}</h2>
          <button onClick={onClose} className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            {/* Name */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('documentPolicies.form.name')}</label>
              <input
                type='text'
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className={cn(inputClassName, errors['name'] && 'border-red-500')}
                placeholder={t('documentPolicies.form.namePlaceholder')}
              />
              {errors['name'] && <p className='mt-1 text-xs text-red-600'>{errors['name']}</p>}
            </div>

            {/* Role */}
            <div>
              <label className={labelClassName}>{t('documentPolicies.form.role')}</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                className={cn(inputClassName, errors['role'] && 'border-red-500')}>
                <option value=''>{t('documentPolicies.form.rolePlaceholder')}</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {t(`jobPostings.roles.${role}`)}
                  </option>
                ))}
              </select>
              {errors['role'] && <p className='mt-1 text-xs text-red-600'>{errors['role']}</p>}
            </div>

            {/* Site */}
            <div>
              <label className={labelClassName}>{t('documentPolicies.form.site')}</label>
              <select
                value={formData.site}
                onChange={(e) => setFormData((prev) => ({ ...prev, site: e.target.value }))}
                className={cn(inputClassName, errors['site'] && 'border-red-500')}>
                {SITE_OPTIONS.map((site) => (
                  <option key={site} value={site}>
                    {site === '_all' ? t('documentPolicies.allSites') : site}
                  </option>
                ))}
              </select>
              {errors['site'] && <p className='mt-1 text-xs text-red-600'>{errors['site']}</p>}
            </div>

            {/* Required Types */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('documentPolicies.form.requiredTypes')}</label>
              <div className='mt-1 flex flex-wrap gap-2'>
                {formData.requiredTypes.map((tag, i) => (
                  <span
                    key={i}
                    className='inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800'>
                    {tag}
                    <button
                      type='button'
                      onClick={() => removeTag('requiredTypes', i)}
                      className='text-blue-600 hover:text-blue-800'>
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type='text'
                value={requiredTypeInput}
                onChange={(e) => setRequiredTypeInput(e.target.value)}
                onKeyDown={(e) => handleTagKeyDown('requiredTypes', requiredTypeInput, setRequiredTypeInput, e)}
                onBlur={() => {
                  addTag('requiredTypes', requiredTypeInput);
                  setRequiredTypeInput('');
                }}
                className={cn(inputClassName, errors['requiredTypes'] && 'border-red-500')}
                placeholder={t('documentPolicies.form.typePlaceholder')}
              />
              {errors['requiredTypes'] && <p className='mt-1 text-xs text-red-600'>{errors['requiredTypes']}</p>}
            </div>

            {/* Optional Types */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('documentPolicies.form.optionalTypes')}</label>
              <div className='mt-1 flex flex-wrap gap-2'>
                {(formData.optionalTypes ?? []).map((tag, i) => (
                  <span
                    key={i}
                    className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700'>
                    {tag}
                    <button
                      type='button'
                      onClick={() => removeTag('optionalTypes', i)}
                      className='text-gray-500 hover:text-gray-700'>
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type='text'
                value={optionalTypeInput}
                onChange={(e) => setOptionalTypeInput(e.target.value)}
                onKeyDown={(e) => handleTagKeyDown('optionalTypes', optionalTypeInput, setOptionalTypeInput, e)}
                onBlur={() => {
                  addTag('optionalTypes', optionalTypeInput);
                  setOptionalTypeInput('');
                }}
                className={inputClassName}
                placeholder={t('documentPolicies.form.typePlaceholder')}
              />
            </div>

            {/* Allowed MIME Types */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('documentPolicies.form.allowedMime')}</label>
              <div className='mt-2 flex flex-wrap gap-4'>
                {MIME_OPTIONS.map((mime) => (
                  <label key={mime} className='flex items-center gap-2 text-sm text-gray-700'>
                    <input
                      type='checkbox'
                      checked={formData.allowedMime.includes(mime)}
                      onChange={() => toggleMime(mime)}
                      className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                    />
                    {mime}
                  </label>
                ))}
              </div>
              {errors['allowedMime'] && <p className='mt-1 text-xs text-red-600'>{errors['allowedMime']}</p>}
            </div>

            {/* Max Size MB */}
            <div>
              <label className={labelClassName}>{t('documentPolicies.form.maxSizeMb')}</label>
              <input
                type='number'
                min={1}
                value={formData.maxSizeMb}
                onChange={(e) => setFormData((prev) => ({ ...prev, maxSizeMb: Number(e.target.value) }))}
                className={cn(inputClassName, errors['maxSizeMb'] && 'border-red-500')}
              />
              {errors['maxSizeMb'] && <p className='mt-1 text-xs text-red-600'>{errors['maxSizeMb']}</p>}
            </div>

            {/* Expiration Threshold Days */}
            <div>
              <label className={labelClassName}>{t('documentPolicies.form.expirationThresholdDays')}</label>
              <input
                type='number'
                min={1}
                value={formData.expirationThresholdDays ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    expirationThresholdDays: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className={inputClassName}
                placeholder={t('documentPolicies.form.expirationPlaceholder')}
              />
            </div>

            {/* OCR Enabled */}
            <div className='sm:col-span-2'>
              <label className='flex items-center gap-3'>
                <input
                  type='checkbox'
                  checked={formData.ocrEnabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, ocrEnabled: e.target.checked }))}
                  className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                />
                <span className='text-sm font-medium text-gray-700'>{t('documentPolicies.form.ocrEnabled')}</span>
              </label>
            </div>
          </div>
        </form>

        <div className='flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting}
            className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            {t('documentPolicies.actions.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
            {isSubmitting ? t('documentPolicies.actions.saving') : t('documentPolicies.actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
